import React, { cloneElement, isValidElement, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Popover } from 'antd';
import { X } from 'lucide-react';
import styled from 'styled-components';
import { TooltipPlacement } from 'antd/lib/tooltip';

import { useStudentMiniCard } from 'core/hooks';
import { useIsMobile, useIsTouch } from 'core/hooks/Layout';
import { MobileIconButton, TouchCardFrame } from 'molecules';

import { StudentMiniCard } from './StudentMiniCard';

interface IProps {
  studentId: string;
  placement?: TooltipPlacement;
  /** 'hover' for list rows / header names, 'click' for the summary strip. */
  trigger?: 'hover' | 'click';
  children: React.ReactNode;
}

/**
 * Popover wrapper that shows the student mini-card around any anchor
 * (spec 0009). The query is deferred until the popover actually opens
 * (`armed`) — the 0.4s enter delay plus the lazy skip mean a mouse
 * travelling down the chat list fires zero requests; reopening hits the
 * Apollo cache.
 */
export const StudentHoverCard = memo<IProps>(
  ({ studentId, placement = 'rightTop', trigger = 'hover', children }) => {
    const [armed, setArmed] = useState(false);
    const [visible, setOpen] = useState(false);
    const { card, loading, failed, refetch } = useStudentMiniCard(studentId, {
      skip: !armed,
    });
    // ТЗ 0121: на сенсорном экране — по нажатию и с крестиком.
    const isTouch = useIsTouch();
    // ТЗ 0121: на телефоне С КАСАНИЕМ — не поповер у якоря, а окно на весь
    // экран (mobile.css): карточка 340 px у края экрана 390 px не
    // помещается. Узкое окно с мышью — прежний поповер по наведению: строка
    // чата под мышью отдельной зоны аватара не получает (`StudentsColumn`
    // делит строку тоже по `useIsTouch`), и нажатие на неё не должно
    // открывать окно поверх чата.
    const isMobile = useIsMobile();
    const asModal = isMobile && isTouch;
    const { t } = useTranslation();
    const miniCard = (
      <StudentMiniCard
        studentId={studentId}
        card={card}
        loading={loading || !armed}
        failed={failed}
        onRetry={refetch}
      />
    );

    // Окно и поповер — порталы, но события React всплывают по дереву
    // компонентов: нажатие внутри карточки дошло бы до строки чата под
    // якорем и открыло бы чат. Гасим всплытие на границе карточки.
    const stop = (event: React.SyntheticEvent) => event.stopPropagation();

    if (asModal) {
      const open = () => {
        setArmed(true);
        setOpen(true);
      };
      const anchor = isValidElement(children)
        ? cloneElement(children as React.ReactElement, {
            onClick: (event: React.MouseEvent) => {
              const own = (children as React.ReactElement).props.onClick;
              if (typeof own === 'function') own(event);
              open();
            },
            'aria-haspopup': 'dialog',
            'aria-expanded': visible,
          })
        : children;
      return (
        <>
          {anchor}
          {/* Пустой span — только граница всплытия для портала окна. */}
          <span onClick={stop} onKeyDown={stop} role="presentation">
            <Modal
              visible={visible}
              // Крестик свой: у antd подпись прибита по-английски («Close»).
              closable={false}
              title={
                <ModalTitle>
                  <span>{t('mobile.communicator.miniCardTitle')}</span>
                  <MobileIconButton
                    icon={X}
                    aria-label={t('button.close')}
                    onClick={() => setOpen(false)}
                  />
                </ModalTitle>
              }
              footer={null}
              destroyOnClose
              onCancel={() => setOpen(false)}
            >
              {miniCard}
            </Modal>
          </span>
        </>
      );
    }

    return (
      <Popover
        content={
          isTouch ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div onClick={stop}>
              <TouchCardFrame onClose={() => setOpen(false)}>
                {miniCard}
              </TouchCardFrame>
            </div>
          ) : (
            miniCard
          )
        }
        overlayClassName={isTouch ? 'erp-touch-card' : undefined}
        placement={placement}
        trigger={isTouch ? 'click' : trigger}
        visible={visible}
        mouseEnterDelay={0.4}
        mouseLeaveDelay={0.1}
        destroyTooltipOnHide
        onVisibleChange={(next) => {
          if (next) setArmed(true);
          setOpen(next);
        }}
      >
        {children}
      </Popover>
    );
  },
);

StudentHoverCard.displayName = 'StudentHoverCard';

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: -8px -8px -8px 0;
`;
